/**
 * `THREE.Points` cloud aggregating "point"-bucket LOD entities (PR-J).
 *
 * # Why a single Points mesh
 *
 * Sub-2-px entities can number in the tens of thousands when the
 * camera is zoomed out on the П35Р fixture. Allocating a separate
 * `THREE.Points` per entity would burn the per-mesh budget the
 * BatchedLayer is engineered to avoid. Instead the LOD pipeline
 * collapses every entity in the "point" bucket into a single dot in
 * one shared `THREE.Points` mesh — one BufferGeometry, one
 * PointsMaterial, drawn in a single GL call.
 *
 * # Per-vertex color
 *
 * AutoCAD honours the entity's resolved color (BYLAYER walked) even
 * for sub-pixel entities — a red layer should still hint red dots at
 * full zoom-out. We carry one `color` attribute per point and set
 * `vertexColors: true` on the material; the per-entity color survives
 * the LOD collapse without touching the material's base color.
 *
 * # `size: 1` and screen-space pixels
 *
 * `THREE.PointsMaterial.size` defaults to world units, which would
 * make the dots invisible at low zoom. We set `sizeAttenuation = false`
 * so `size = 1` means *one CSS pixel*, regardless of zoom — exactly
 * the visual budget the LOD policy guaranteed.
 *
 * # Test boundary (jsdom / no GL)
 *
 * The unit tests for the LOD policy do not import this file — it
 * pulls in `three` namespace + GL features that would crash in jsdom.
 * Builders / disposers are exposed for the renderer host
 * (`Scene3DStage.tsx`) only.
 */

import * as THREE from "three";

import { entityBboxCenter } from "./visibleSet";
import type { RenderableEntity } from "../types";

/**
 * Build a `THREE.Points` mesh for the given "point"-bucket entities.
 * Returns `null` when the bucket is empty so callers can skip
 * `scene.add` without an empty-buffer warning from THREE.
 *
 * `userData` is merged UNDER the mandatory `kind: "lod-points-cloud"` marker —
 * the attribution stamp for a per-viewport proxy cloud
 * (`rendering/viewportPointProxyStamp.ts viewportPointProxyUserData`). Omit it
 * for the main camera's own cloud.
 *
 * The mesh is opaque, depth-write enabled, and frustum-culled off
 * (the LOD pipeline already culled via the spatial index — the GPU
 * cull step would only re-traverse the work).
 */
export function buildPointsCloud(
  entities: ReadonlyArray<RenderableEntity>,
  userData?: Readonly<Record<string, unknown>>,
): THREE.Points | null {
  if (entities.length === 0) return null;

  const positions = new Float32Array(entities.length * 3);
  const colors = new Float32Array(entities.length * 3);
  const tmp = new THREE.Color();
  for (let i = 0; i < entities.length; i++) {
    const entity = entities[i]!;
    const center = entityBboxCenter(entity);
    positions[i * 3] = center.x;
    positions[i * 3 + 1] = center.y;
    positions[i * 3 + 2] = center.z;
    tmp.set(entity.colorHex);
    colors[i * 3] = tmp.r;
    colors[i * 3 + 1] = tmp.g;
    colors[i * 3 + 2] = tmp.b;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

  const material = new THREE.PointsMaterial({
    // 1 CSS pixel — matches the LOD bucket's promise (< 2 px ≈ 1 dot).
    size: 1,
    sizeAttenuation: false,
    vertexColors: true,
    transparent: false,
    depthWrite: true,
  });

  const points = new THREE.Points(geometry, material);
  points.frustumCulled = false;
  // Codex PR #784 round-12 P2 — `kind` is what every "is this a points cloud"
  // consumer keys on and is never overridden; `userData` adds the ATTRIBUTION
  // stamp for a per-viewport proxy cloud
  // (`rendering/viewportPointProxyStamp.ts`). The main camera's cloud passes
  // nothing and keeps exactly the userData it has always carried.
  points.userData = { ...userData, kind: "lod-points-cloud" };
  return points;
}

/**
 * Dispose helper — releases GPU resources for a points cloud built
 * by `buildPointsCloud`. Safe to call on `null` for symmetric callers.
 */
export function disposePointsCloud(points: THREE.Points | null): void {
  if (!points) return;
  points.geometry.dispose();
  const mat = points.material as THREE.Material | THREE.Material[];
  if (Array.isArray(mat)) {
    for (const m of mat) m.dispose();
  } else {
    mat.dispose();
  }
}
